import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { notBlank } from "../../../../util/ext";
import {
  useModuleUpdate,
  useModuleDelete,
} from "../../../modules/moduleServices";
import {
  useCreateActiveModule,
  useDeleteActiveModule,
} from "../../../../manage-organizations/organizationServices";
import { pluck } from "ramda";
import { isActive } from "../../../modules/util";
import { useSelector } from "react-redux";
import { useSnackAlert } from "../../../../common/use-snackAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { GroupBrowserContext } from "../../OrganizeView";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";

import DeleteDialog from "../../../common/DeleteDialog";
import StatusBadge from "../../../common/StatusBadge";
import { useMediaPermissions } from "../../../../util/mediaUtil";
import { useCurrentOrgId } from "../../../../auth/authUtils";
import { useAlert } from "../../OrganizeProvider";
import PublishStatusDialog from "../../../modules/edit/PublishStatusDialog";
import { useNavigate, useLocation } from "react-router-dom";

const statusBadgeLabel = {
  ACTIVE: "Push Live",
  INACTIVE: "Make Draft",
};

const ModuleOtherActions = React.forwardRef((props, ref) => {
  const { module, clearSelected } = props;
  const auth = useSelector(state => state.auth);
  const currentOrgId = useCurrentOrgId();
  const alert = useAlert();
  const browser = useContext(GroupBrowserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { SnackAlert, showAlert } = useSnackAlert();
  const { deletable, readOnly } = useMediaPermissions(module);

  const [statusDialog, setStatusDialog] = useState(false);
  const [oppositeStatus, setOppositeStatus] = useState("");
  useEffect(() => {
    if (notBlank(module)) {
      setOppositeStatus(module.status === "INACTIVE" ? "ACTIVE" : "INACTIVE");
    }
  }, [module]);

  const updateModule = useModuleUpdate();
  const createActiveModule = useCreateActiveModule();
  const deleteActiveModule = useDeleteActiveModule();
  const changeStatus = useCallback(async () => {
    browser.setLoading(true);
    try {
      setStatusDialog(false);
      const newStatus = isActive(module.status) ? "INACTIVE" : "ACTIVE";

      if (notBlank(auth.currentOrganization)) {
        const variables = {
          resourceIds: {
            moduleId: module.id,
            orgId: currentOrgId,
          },
        };
        if (isActive(newStatus)) {
          await createActiveModule.mutateAsync(variables);
        } else {
          await deleteActiveModule.mutateAsync(variables);
        }

        await updateModule.mutateAsync({
          resourceIds: { id: module.id },
          data: {
            status: newStatus,
          },
        });
      }
    } catch (err) {
      const messages = notBlank(err.body.errors)
        ? pluck("message", err.body.errors)
        : [
            "There was an issue changing the status of this module. Please try again later.",
          ];
      showAlert("error", messages);
    }
    clearSelected();
    browser.setLoading(false);
  }, [
    auth.currentOrganization,
    browser,
    clearSelected,
    createActiveModule,
    currentOrgId,
    deleteActiveModule,
    updateModule,
    module,
    showAlert,
  ]);

  const handleEditClick = () => {
    const { pathname, search } = location;
    const fromPath = notBlank(search) ? pathname + search : pathname;
    navigate(`/creator/modules/${module.id}`, {
      state: {
        from: fromPath,
      },
    });
  };

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    changeStatus();
  };

  const handleUndo = previousModule => async () => {
    browser.setLoading(true);
    await createActiveModule.mutateAsync({
      resourceIds: {
        moduleId: previousModule.id,
        orgId: previousModule.orgId,
      },
    });

    await updateModule.mutateAsync({
      resourceIds: { id: previousModule.id },
      data: { status: previousModule.status },
    });
    browser.setLoading(false);
  };

  const deleteModule = useModuleDelete();
  const handleDelete = async () => {
    await deleteModule.mutateAsync({ module: module, orgId: currentOrgId });

    alert.update({
      actionButtonLabel: "undo",
      showAction: true,
      onAlertClick: handleUndo({ ...module, orgId: currentOrgId }),
    });
  };

  return (
    <Fragment>
      <SnackAlert />

      <PublishStatusDialog
        open={statusDialog}
        action={module.status === "ACTIVE" ? "unpublish" : "publish"}
        handleStatusChange={handleClick}
        handleClickClose={() => setStatusDialog(false)}
      />

      <MenuItem ref={ref} onClick={() => setStatusDialog(true)}>
        <ListItemIcon>
          <StatusBadge size="xs" status={oppositeStatus} />
        </ListItemIcon>

        <ListItemText>{statusBadgeLabel[oppositeStatus]}</ListItemText>
      </MenuItem>

      {!readOnly && (
        <MenuItem
          onClick={event => {
            event.stopPropagation();
            handleEditClick();
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={faPen} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}

      {deletable && (
        <DeleteDialog
          handleDelete={handleDelete}
          renderButton={onDelete => (
            <MenuItem
              ref={ref}
              onClick={event => {
                event.stopPropagation();
                onDelete(clearSelected);
              }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faTrashCan} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        />
      )}
    </Fragment>
  );
});

export default ModuleOtherActions;
